import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { getProducts } from "@/lib/api";
import { IMG, CATEGORIES, SPECIES, MOULDING_PROFILES } from "@/data/site";
import DivisionHero from "@/components/DivisionHero";
import ProductCard from "@/components/ProductCard";
import QuoteCTA from "@/components/QuoteCTA";
import { Reveal, Kicker } from "@/components/Reveal";

export default function Timber() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    getProducts({ division: "timber" }).then(setProducts);
  }, []);

  return (
    <div data-testid="timber-page">
      <DivisionHero
        kicker="Division 01 — Timber"
        lines={["Kwila & Tropical", "Hardwood Timber Supply"]}
        intro="Mr Kwila supplies Kwila and selected tropical hardwood timber products for joinery, door manufacturing, decking, structural use, builders, merchants and timber manufacturers. Timber is supplied quote-first — tell us your species, sizes and volumes."
        image="/timber-hero.png"
        imageAlt="Vitex hardwood decking overlooking the ocean"
        brochure={{ label: "Mr Kwila Timber — Product Brochure", pdf: "/brochures/mr-kwila-timber-brochure.pdf", cover: "/brochures/timber-cover.png" }}
      />

      <section className="px-5 pb-24 md:px-10" data-testid="timber-categories">
        <div className="mx-auto max-w-[1500px]">
          <Reveal className="mb-8">
            <Kicker>Timber Categories</Kicker>
            <h2 className="mt-3 font-display text-3xl font-light tracking-tight md:text-5xl">Supply pathways</h2>
          </Reveal>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {CATEGORIES.timber.map((c, i) => (
              <Reveal key={c} delay={i * 0.04}>
                <Link
                  to={`/shop?division=timber&category=${encodeURIComponent(c)}`}
                  data-testid={`timber-category-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="group flex h-full flex-col justify-between gap-6 rounded-sm border border-ink/15 p-5 transition-colors hover:bg-ink hover:text-cream"
                >
                  <span className="text-xs font-bold text-kwila group-hover:text-cream/60">0{i + 1}</span>
                  <span className="flex items-end justify-between gap-2">
                    <span className="font-display text-xl leading-tight md:text-2xl">{c}</span>
                    <ArrowUpRight size={16} className="shrink-0 text-sand group-hover:text-cream" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink px-5 py-24 text-cream md:px-10" data-testid="mouldings-section">
        <div className="mx-auto max-w-[1500px]">
          <Reveal className="mb-12 flex flex-wrap items-end justify-between gap-6">
            <div>
              <Kicker dark>Export Profiles</Kicker>
              <h2 className="mt-3 font-display text-3xl font-light tracking-tight md:text-5xl">
                Timber mouldings
              </h2>
            </div>
            <p className="max-w-sm text-sm leading-relaxed text-cream/60">
              Machined in our PNG mills to the full export profile chart — flooring, decking, skirtings,
              handrails, dowels and more. All profiles quoted to your volumes and lengths.
            </p>
          </Reveal>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="mouldings-grid">
            {MOULDING_PROFILES.map((m, i) => (
              <Reveal key={m.slug} delay={(i % 4) * 0.05} className="h-full">
                <Link
                  to={`/timber/mouldings/${m.slug}`}
                  data-testid={`moulding-card-${m.slug}`}
                  className="group flex h-full flex-col rounded-sm bg-cream p-4 transition-shadow hover:ring-2 hover:ring-kwila"
                >
                  <div className="flex h-20 items-center justify-center">
                    <img
                      src={m.img}
                      alt={`${m.name} profile drawing`}
                      loading="lazy"
                      className="max-h-full w-full object-contain"
                    />
                  </div>
                  <p className="mt-3 border-t border-ink/10 pt-2.5 text-[12px] font-bold uppercase tracking-[0.1em] text-ink">
                    {m.name}
                  </p>
                  <p className="mt-1 text-[11px] leading-relaxed tracking-wide text-sand">
                    {m.sizes.join("  ·  ")}
                  </p>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1} className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/product/timber-mouldings"
              data-testid="mouldings-product-link"
              className="flex items-center gap-2 rounded-full bg-cream px-7 py-3 text-xs font-bold uppercase tracking-[0.14em] text-ink transition-colors hover:bg-kwila hover:text-cream"
            >
              View Mouldings Product <ArrowRight size={14} />
            </Link>
            <Link
              to="/request-quote"
              data-testid="mouldings-quote-link"
              className="flex items-center gap-2 rounded-full border border-cream/40 px-7 py-3 text-xs font-bold uppercase tracking-[0.14em] transition-colors hover:bg-cream hover:text-ink"
            >
              Quote Mouldings
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="bg-stone/60 px-5 py-24 md:px-10" data-testid="timber-species-index">
        <div className="mx-auto max-w-[1500px]">
          <Reveal className="mb-10 flex flex-wrap items-end justify-between gap-6">
            <div>
              <Kicker>Species Index</Kicker>
              <h2 className="mt-3 font-display text-3xl font-light tracking-tight md:text-5xl">
                PNG hardwood species
              </h2>
            </div>
            <p className="max-w-sm text-sm leading-relaxed text-ink/60">
              Detailed species properties are being finalised with our milling team — each page notes what is
              confirmed and what is to follow.
            </p>
          </Reveal>
          <div className="border-t border-ink/10">
            {SPECIES.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.04}>
                <Link
                  to={`/timber/species/${s.slug}`}
                  data-testid={`species-link-${s.slug}`}
                  className="group flex flex-wrap items-baseline justify-between gap-3 border-b border-ink/10 py-6 transition-colors hover:bg-cream/60"
                >
                  <div className="flex items-baseline gap-5">
                    <span className="text-xs font-bold text-kwila">0{i + 1}</span>
                    <span className="font-display text-3xl tracking-tight transition-transform duration-300 group-hover:translate-x-2 md:text-4xl">
                      {s.name}
                    </span>
                  </div>
                  <span className="font-display text-lg italic text-sand">{s.latin}</span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>


      <section className="px-5 py-24 md:px-10" data-testid="timber-products">
        <div className="mx-auto max-w-[1500px]">
          <Reveal className="mb-12 flex flex-wrap items-end justify-between gap-6">
            <div>
              <Kicker>From the Yard</Kicker>
              <h2 className="mt-3 font-display text-3xl font-light tracking-tight md:text-5xl">Timber products</h2>
            </div>
            <Link
              to="/shop?division=timber"
              data-testid="timber-view-all-link"
              className="flex items-center gap-2 rounded-full border border-ink/30 px-6 py-3 text-xs font-bold uppercase tracking-[0.14em] transition-colors hover:bg-ink hover:text-cream"
            >
              View All Timber <ArrowRight size={14} />
            </Link>
          </Reveal>
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {products.slice(0, 4).map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
          <Reveal className="mt-12 rounded-sm border border-kwila/30 bg-kwila/5 p-6 md:p-8" data-testid="timber-quote-note">
            <p className="text-sm leading-relaxed text-ink/75 md:text-base">
              <span className="font-bold text-kwila">Quote-first supply.</span> Timber, bulk orders and packs are
              priced on request. Send your cutting list, species, sizes, quantities and delivery location — trade
              accounts available for joiners, door manufacturers, merchants and builders.
            </p>
          </Reveal>
        </div>
      </section>

      <QuoteCTA
        title="Need timber priced to your cutting list?"
        copy="Send species, dimensions, quantities and delivery location in one request. Our Archerfield team will confirm availability, lead time and freight."
      />
    </div>
  );
}
